import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { ErrorHandler, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BehaviorsModule } from '@ddv/behaviors';
import { CommonComponentsModule } from '@ddv/common-components';
import { DashboardsModule } from '@ddv/dashboards';
import { ErrorHandlerService, ErrorHandlingModule } from '@ddv/error-handling';
import { HttpModule, ApiConfiguration, legacyUatDomain, uatDomain, legacyProdDomain, prodDomain } from '@ddv/http';
import { ManageWidgetsModule } from '@ddv/manage-widgets';
import { ReferenceDataModule } from '@ddv/reference-data';
import { SocketModule } from '@ddv/socket';
import { ViewsModule } from '@ddv/views';
import { VisualizationsModule } from '@ddv/visualizations';
import { ApmModule, PublicApiEnvironment } from '@hs/ui-core-common';
import { CcDatesModule } from '@hs/ui-core-date';
import { CcFundModule } from '@hs/ui-core-fund';
import { ModalLayerComponent } from '@hs/ui-core-layout';
import { CcNavbarModule, NavBarComponent } from '@hs/ui-core-navbar';
import { VersionUpdateNotifierComponent } from '@hs/ui-core-notifications';
import { TooltipComponent } from '@hs/ui-core-presentation';

import { AppComponent } from './app.component';
import { ApplicationService } from './application.service';
import { environment } from '../environments/environment';
import { MainComponent } from './main/main.component';
import { AppRoutingModule } from './routing/app-routing.module';
import { WrapperModule } from './wrapper/wrapper.module';

@NgModule({
    imports: [
        ApmModule.forRoot({
            serviceName: 'DDV UI',
            active: environment.apm.active,
        }),
        AppRoutingModule,
        BehaviorsModule,
        BrowserAnimationsModule,
        CcDatesModule.forRoot(
            getPublicApiEnvironment(window.location.hostname, environment.isEdge),
            environment.trebekApiLocation.hostAndPort,
        ),
        CcFundModule.forRoot(
            getPublicApiEnvironment(window.location.hostname, environment.isEdge),
            environment.trebekApiLocation.hostAndPort,
        ),
        CommonComponentsModule,
        DashboardsModule,
        ErrorHandlingModule,
        HttpModule,
        ManageWidgetsModule,
        ReferenceDataModule,
        SocketModule,
        ViewsModule,
        VisualizationsModule,
        WrapperModule,
        CcNavbarModule,
        ModalLayerComponent,
        NavBarComponent,
        TooltipComponent,
        VersionUpdateNotifierComponent,
    ],
    declarations: [
        AppComponent,
        MainComponent,
    ],
    providers: [
        ApplicationService,
        { provide: LocationStrategy, useClass: HashLocationStrategy },
        {
            provide: 'apiConfig',
            useFactory: (): ApiConfiguration => environment,
        },
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        provideZoneChangeDetection({
            eventCoalescing: true,
            runCoalescing: true,
        }),
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}

export function getPublicApiEnvironment(hostname: string, isEdge: boolean): PublicApiEnvironment | 'local' {
    if (hostname === 'localhost') {
        return 'local';
    }

    if (hostname.includes('-dr')) {
        return 'dr';
    }

    if (hostname.includes(`app-qa.${legacyUatDomain}`) || hostname.includes(`app-qa.${uatDomain}`)) {
        return 'qa';
    }

    if (hostname.includes(`.${legacyProdDomain}`) || hostname.includes(`.${prodDomain}`)) {
        return hostname.includes('app-tst') ? 'tst' : 'prod';
    }

    return isEdge ? 'edge' : 'stable';
}
